import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { calculationConverter } from '@seu-converter';
import { RequestService, StoreService, ToastService } from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuActiveCalculation,
  SeuCalculationPayload,
  SeuCalculationResponseApi,
  SeuCalculationResponseItem,
} from '@seu-types';
import {
  Subject,
  catchError,
  firstValueFrom,
  map,
  of,
  race,
  take,
  timer,
} from 'rxjs';
import { SeuApiComputationErrorResponseDto } from 'src/app/shared/api';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  private readonly requestService = inject(RequestService);
  private readonly store = inject(StoreService);
  private readonly toastService = inject(ToastService);

  private readonly cancelPolling$$ = new Subject<void>();

  public cancelAllPolling(): void {
    this.cancelPolling$$.next();
  }

  public async doCalculation(
    offerId: string,
    activeCalculation: SeuActiveCalculation
  ): Promise<boolean> {
    const endpoint = SEU_API_ENDPOINTS.calculation.replace(':offerId', offerId);

    const data: SeuCalculationPayload = {
      computationInput: {
        globalParameterSetId: activeCalculation.globalParameterSetId,
        interconnectionId: activeCalculation.interconnectionId,
        operatingConditionId: activeCalculation.operatingConditionId,
      },
    };

    while (true) {
      const responseData = await firstValueFrom(
        this.requestService
          .post<SeuCalculationResponseApi>(endpoint as SEU_API_ENDPOINTS, data)
          .pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 400) {
                const errorBody = error.error as
                  | SeuApiComputationErrorResponseDto
                  | undefined;

                this.store.addCalculationResponseItem(
                  this.toErrorResponseItem(
                    activeCalculation,
                    errorBody?.messages ?? []
                  )
                );

                return of(null);
              }

              this.toastService.addToast({
                type: 'error',
                text: 'calculation.toasts.calculation-error',
              });

              return of(null);
            })
          )
      );

      if (!responseData) {
        return false;
      }

      const calculationResponseItem =
        calculationConverter.fromApi(responseData);

      if (!calculationResponseItem) {
        console.error('Calculation response is not valid');
        return false;
      }

      if (calculationResponseItem.pollingIntervalSeconds === 0) {
        this.store.addCalculationResponseItem({
          ...calculationResponseItem,
          uid: activeCalculation.uid,
        });
        return true;
      }

      const outcome = await firstValueFrom(
        race(
          timer(calculationResponseItem.pollingIntervalSeconds * 1000).pipe(
            map(() => 'continue' as const)
          ),
          this.cancelPolling$$.pipe(
            take(1),
            map(() => 'cancelled' as const)
          )
        )
      );

      if (outcome === 'cancelled') {
        return false;
      }
    }
  }

  private toErrorResponseItem(
    activeCalculation: SeuActiveCalculation,
    computationErrorLabelKeys: string[]
  ): SeuCalculationResponseItem {
    return {
      uid: activeCalculation.uid,
      globalParameterSetId: String(activeCalculation.globalParameterSetId),
      interconnectionId: String(activeCalculation.interconnectionId),
      operatingConditionId: String(activeCalculation.operatingConditionId),
      computationState: 'error',
      components: [],
      streams: [],
      globalCodes: [],
      computationErrorLabelKeys,
      opex: null,
      emissionLimits: null,
      pollingIntervalSeconds: 0,
    };
  }
}
